import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Lightbulb, ListTodo, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { getReportsForDatabase, listPlans, updatePlan } from '../services/api';
import { RecommendedPrioritiesFullPanel } from '../components/reports/RecommendedPrioritiesFullPanel';

/**
 * Full-page recommended priorities for a database (layout aligned with Implementation Plans).
 */
export const ReportPrioritiesPage = () => {
    const { t } = useTranslation(['pages-misc-1', 'common']);
    const { databaseId } = useParams();
    const navigate = useNavigate();
    const { databases } = useDatabase();
    const { showToast } = useToast();

    const database = databases.find((db) => db.id === parseInt(databaseId, 10));

    const [reportsList, setReportsList] = useState([]);
    const [allDatabasePlans, setAllDatabasePlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(true);
    const [priorityFilter, setPriorityFilter] = useState('');
    const [topicFilter, setTopicFilter] = useState('');
    const [timeFrameFilter, setTimeFrameFilter] = useState('');
    const [includeArchivedPlans, setIncludeArchivedPlans] = useState(false);

    /** Stats use every plan for this database (including archived); list view still respects Active/All. */
    const stats = useMemo(() => {
        const total = allDatabasePlans.length;
        const active = allDatabasePlans.filter((p) => p.status === 'active').length;
        const implemented = allDatabasePlans.filter((p) => p.implemented).length;
        const evaluated = allDatabasePlans.filter((p) => p.latest_evaluation).length;
        return { total, active, implemented, evaluated };
    }, [allDatabasePlans]);

    const fetchReportsList = useCallback(async () => {
        if (!databaseId) return;
        try {
            const response = await getReportsForDatabase(databaseId);
            const reports = response.data?.data?.reports || [];
            setReportsList(reports);
        } catch (error) {
            console.error('Failed to fetch reports list:', error);
        }
    }, [databaseId]);

    const fetchAllPlans = useCallback(async () => {
        if (!databaseId) return;
        setPlansLoading(true);
        try {
            const response = await listPlans({
                databaseId: parseInt(databaseId, 10),
                status: 'all',
            });
            setAllDatabasePlans(response.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch database plans:', error);
            setAllDatabasePlans([]);
        } finally {
            setPlansLoading(false);
        }
    }, [databaseId]);

    useEffect(() => {
        fetchReportsList();
    }, [fetchReportsList]);

    useEffect(() => {
        fetchAllPlans();
    }, [fetchAllPlans]);

    const handleSetPlanStatus = async (planId, nextStatus) => {
        try {
            await updatePlan(planId, { status: nextStatus });
            showToast(nextStatus === 'archived' ? t('pages-misc-1:reportPrioritiesPage.planArchived') : t('pages-misc-1:reportPrioritiesPage.planUnarchived'), 'success');
            await fetchAllPlans();
        } catch (error) {
            console.error('Failed to update plan status:', error);
            showToast(t('pages-misc-1:reportPrioritiesPage.updatePlanStatusFailed'), 'error');
        }
    };

    const backToReports = () => navigate(`/reports/${databaseId}`);

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="bg-white border-b border-gray-200 py-6 px-page">
                <div className="page-content">
                    <div className="flex items-start gap-3">
                        <button
                            type="button"
                            onClick={backToReports}
                            className="text-gray-400 hover:text-gray-600 mt-1 shrink-0"
                            aria-label={t('pages-misc-1:reportPrioritiesPage.backToReports')}
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div className="flex items-start gap-3 min-w-0">
                            <Lightbulb className="w-8 h-8 text-amber-500 shrink-0 hidden sm:block mt-0.5" />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                                    {t('pages-misc-1:reportPrioritiesPage.title')}
                                </h1>
                                <p className="text-gray-600 mt-2 text-sm sm:text-base">
                                    {database?.name ? `${database.name} · ` : ''}
                                    {t('pages-misc-1:reportPrioritiesPage.subtitleSuffix')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-shell">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                                <ListTodo className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">{t('pages-misc-1:reportPrioritiesPage.totalPriorities')}</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center shrink-0">
                                <Clock className="w-6 h-6 text-orange-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">{t('pages-misc-1:reportPrioritiesPage.active')}</p>
                                <p className="text-3xl font-bold text-orange-600">{stats.active}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center shrink-0">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">{t('pages-misc-1:reportPrioritiesPage.implemented')}</p>
                                <p className="text-3xl font-bold text-green-600">{stats.implemented}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center shrink-0">
                                <AlertCircle className="w-6 h-6 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">{t('pages-misc-1:reportPrioritiesPage.evaluated')}</p>
                                <p className="text-3xl font-bold text-purple-600">{stats.evaluated}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <RecommendedPrioritiesFullPanel
                    databaseId={databaseId}
                    navigate={navigate}
                    allDatabasePlans={allDatabasePlans}
                    reportsList={reportsList}
                    priorityFilter={priorityFilter}
                    setPriorityFilter={setPriorityFilter}
                    topicFilter={topicFilter}
                    setTopicFilter={setTopicFilter}
                    timeFrameFilter={timeFrameFilter}
                    setTimeFrameFilter={setTimeFrameFilter}
                    includeArchivedPlans={includeArchivedPlans}
                    setIncludeArchivedPlans={setIncludeArchivedPlans}
                    handleSetPlanStatus={handleSetPlanStatus}
                    loading={plansLoading}
                />
            </div>
        </div>
    );
};

export default ReportPrioritiesPage;
